class OmoMobileNav extends HTMLElement {
  constructor() {
    super();
    this.mobileMedia = window.matchMedia('(max-width: 989px)');
    this.panelTimers = new Map();
  }

  connectedCallback() {
    this.toggleButton = this.querySelector('.omo-mobile-nav__toggle');
    this.overlay = this.querySelector('.omo-mobile-nav__overlay');
    this.openLabel = this.querySelector('.omo-mobile-nav__open-label');
    this.closeLabel = this.querySelector('.omo-mobile-nav__close-label');
    this.primaryControls = [...this.querySelectorAll('[data-omo-mobile-submenu]')];
    this.panels = [...this.querySelectorAll('[data-omo-mobile-panel]')];
    this.header = this.closest('.header');

    if (!this.toggleButton || !this.overlay) return;

    this.onToggleClick = () => {
      if (this.isOpen) {
        this.close(true);
        return;
      }

      this.closeSearch();
      this.open();
    };
    this.onKeyDown = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close(true);
      }
    };
    this.onMediaChange = (event) => {
      if (!event.matches) this.close(false, true);
    };
    this.onPageHide = () => this.close(false, true);
    this.onHeaderAction = (event) => {
      if (!this.isOpen) return;
      if (event.target.closest('.header__search summary, .header__icon--cart')) this.close(false, true);
    };

    this.toggleButton.addEventListener('click', this.onToggleClick);
    this.primaryControls.forEach((control) => {
      control.addEventListener('click', () => this.toggleSubmenu(control));
    });
    this.overlay.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.close(false, true));
    });
    document.addEventListener('keydown', this.onKeyDown);
    this.header?.addEventListener('click', this.onHeaderAction, true);
    this.mobileMedia.addEventListener('change', this.onMediaChange);
    window.addEventListener('pagehide', this.onPageHide);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.onToggleClick);
    document.removeEventListener('keydown', this.onKeyDown);
    this.header?.removeEventListener('click', this.onHeaderAction, true);
    this.mobileMedia.removeEventListener('change', this.onMediaChange);
    window.removeEventListener('pagehide', this.onPageHide);
    this.unlockScroll();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    if (!this.mobileMedia.matches || this.isOpen) return;

    clearTimeout(this.closeTimer);
    this.overlay.inert = false;
    this.overlay.setAttribute('aria-hidden', 'false');
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.openLabel.hidden = true;
    this.closeLabel.hidden = false;
    this.lockScroll();
  }

  closeSearch() {
    const search = this.header?.querySelector('.header__icons > details-modal.header__search');
    if (search?.isOpen()) search.close(false);
  }

  close(returnFocus = false, immediate = false) {
    if (!this.isOpen && !this.classList.contains('is-closing')) return;

    clearTimeout(this.closeTimer);
    this.classList.remove('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.openLabel.hidden = false;
    this.closeLabel.hidden = true;
    this.closeAllSubmenus(immediate);
    this.unlockScroll();

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishClose();
    } else {
      this.classList.add('is-closing');
      this.closeTimer = setTimeout(() => this.finishClose(), 180);
    }

    if (returnFocus) this.toggleButton.focus();
  }

  finishClose() {
    this.classList.remove('is-closing');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.inert = true;
  }

  toggleSubmenu(control) {
    const panelId = control.dataset.omoMobileSubmenu;
    const panel = this.panels.find((item) => item.dataset.omoMobilePanel === panelId);
    if (!panel) return;

    if (control.getAttribute('aria-expanded') === 'true') {
      this.closeSubmenu(control, panel);
      return;
    }

    this.primaryControls.forEach((otherControl) => {
      const otherPanel = this.panels.find(
        (item) => item.dataset.omoMobilePanel === otherControl.dataset.omoMobileSubmenu
      );
      if (otherPanel) this.closeSubmenu(otherControl, otherPanel);
    });

    const pendingTimer = this.panelTimers.get(panel);
    if (pendingTimer) clearTimeout(pendingTimer);
    panel.hidden = false;
    panel.inert = false;
    panel.setAttribute('aria-hidden', 'false');
    control.setAttribute('aria-expanded', 'true');
    control.classList.add('is-active');
    requestAnimationFrame(() => panel.classList.add('is-active'));
  }

  closeSubmenu(control, panel, immediate = false) {
    const pendingTimer = this.panelTimers.get(panel);
    if (pendingTimer) clearTimeout(pendingTimer);

    control.setAttribute('aria-expanded', 'false');
    control.classList.remove('is-active');
    panel.classList.remove('is-active');
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.hidden = true;
      return;
    }

    const timer = setTimeout(() => {
      if (!panel.classList.contains('is-active')) panel.hidden = true;
      this.panelTimers.delete(panel);
    }, 200);
    this.panelTimers.set(panel, timer);
  }

  closeAllSubmenus(immediate = false) {
    this.primaryControls.forEach((control) => {
      const panel = this.panels.find(
        (item) => item.dataset.omoMobilePanel === control.dataset.omoMobileSubmenu
      );
      if (panel) this.closeSubmenu(control, panel, immediate);
    });
  }

  lockScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--omo-mobile-scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('omo-mobile-nav-open');
  }

  unlockScroll() {
    document.body.classList.remove('omo-mobile-nav-open');
    document.documentElement.style.removeProperty('--omo-mobile-scrollbar-width');
  }
}

if (!customElements.get('omo-mobile-nav')) {
  customElements.define('omo-mobile-nav', OmoMobileNav);
}
